/**
 * SPA-side adapters for `makeSandbox`. Bridges the leaf sandbox.ts module
 * to Convex (live mutations for signed-in users, demo mutations + demo
 * queries when ?demo=1).
 *
 * Kept separate from sandbox.ts so the latter stays importable from a
 * future CLI / Node script without pulling React or Convex into the leaf.
 *
 * Memory: project_repancy_demo_in_convex.md, MILESTONE-A.md §A.7
 */

import type { ConvexReactClient } from 'convex/react';
import type { api } from '../../../convex/_generated/api';
import type {
  AuditWriter,
  DemoFixtureProvider,
  MeResponse,
  GroupsResponse,
  PermissionsResponse,
  RoundTripResult,
  RoundTripStep,
  SandboxEvent,
  SandboxSummary,
  SitesResponse,
} from './sandbox';

type ConvexLike = Pick<ConvexReactClient, 'mutation' | 'query'>;
type Api = typeof api;

export function makeConvexAuditWriter(
  convex: ConvexLike,
  isDemo: boolean,
  showcaseSessionId: string | undefined,
  apiRefs: Api,
): AuditWriter {
  return {
    async write(event: SandboxEvent, summary: SandboxSummary): Promise<void> {
      if (isDemo) {
        if (!showcaseSessionId) {
          throw new Error('sandboxWiring: showcaseSessionId required when isDemo=true');
        }
        await convex.mutation(apiRefs.demoSharepointSandboxAudit.append, {
          demoSessionId: showcaseSessionId,
          event,
          summary,
        });
      } else {
        await convex.mutation(apiRefs.sharepointSandboxAudit.append, {
          event,
          summary,
        });
      }
    },
  };
}

// Inline fixtures for everything except sites — the demo sites table is the
// canonical source so the wizard tree and the sandbox panel stay in sync.
const DEMO_ME: MeResponse = {
  id: 'demo-me',
  displayName: 'Demo User',
  userPrincipalName: 'demo.user@areweai.onmicrosoft.com',
  mail: 'demo.user@areweai.onmicrosoft.com',
  jobTitle: 'Demo attorney',
  officeLocation: 'Demo office',
};

const DEMO_GROUPS: GroupsResponse = {
  value: [
    { id: 'demo-grp-1', displayName: 'Litigation team', '@odata.type': '#microsoft.graph.group' },
    { id: 'demo-grp-2', displayName: 'M&A team', '@odata.type': '#microsoft.graph.group' },
    {
      id: 'demo-grp-3',
      displayName: 'Compliance officers',
      '@odata.type': '#microsoft.graph.directoryRole',
    },
  ],
};

const DEMO_PERMISSIONS: PermissionsResponse = {
  value: [
    {
      id: 'demo-perm-owner',
      roles: ['owner'],
      grantedTo: {
        user: { displayName: 'Demo User', email: 'demo.user@areweai.onmicrosoft.com' },
      },
    },
    {
      id: 'demo-perm-read',
      roles: ['read'],
      grantedToIdentities: [
        { user: { displayName: 'Outside counsel', email: 'counsel@areweai.onmicrosoft.com' } },
      ],
    },
  ],
};

function buildDemoRoundTrip(): RoundTripResult {
  const steps: RoundTripStep[] = [
    { label: 'Acquire MSAL access token', status: 'ok', detail: '(fixture)' },
    { label: 'PUT scratch file content', status: 'ok', detail: '(fixture) id=demo-item-…' },
    { label: 'GET file metadata', status: 'ok', detail: '(fixture) 57b' },
    { label: 'GET file content back', status: 'ok', detail: '(fixture) content matches' },
    { label: 'DELETE scratch file', status: 'ok', detail: '(fixture) cleanup OK' },
  ];
  return { steps, success: true, fileSize: 57 };
}

export function makeConvexFixtures(
  convex: ConvexLike,
  apiRefs: Api,
  showcaseSessionId: string,
): DemoFixtureProvider {
  return {
    async getMe() {
      return DEMO_ME;
    },
    async getMyGroups() {
      return DEMO_GROUPS;
    },
    async listSites(term: string): Promise<SitesResponse> {
      const rows = await convex.query(apiRefs.demoSharepoint.listSites, { demoSessionId: showcaseSessionId });
      const lower = term.toLowerCase();
      const filtered = rows.filter((r) =>
        r.displayName.toLowerCase().includes(lower),
      );
      return {
        value: filtered.map((r) => ({
          id: r.siteId,
          displayName: r.displayName,
          webUrl: r.webUrl,
        })),
      };
    },
    async getItemPermissions() {
      return DEMO_PERMISSIONS;
    },
    async runOneDriveRoundTrip() {
      return buildDemoRoundTrip();
    },
  };
}
